/**
 * Класс для управления кастомным селектором языка
 * Реализует функциональность выбора языка с кликом по соответствующему элементу меню
 */

// Константы селекторов
const SELECTORS = {
  TRIGGER: ".language-selector__trigger",
  DROPDOWN: ".language-selector__dropdown",
  OPTIONS: ".language-selector__option",
  CURRENT: ".language-selector__current",
  DESKTOP_MENU_LINK: ".desktop-menu__link, a[href]",
  LANGUAGE_SELECTOR: ".language-selector",
  LANGUAGE_SELECTOR_OPEN: "language-selector--open",
  LANGUAGE_SELECTOR_MOBILE: "language-selector--mobile",
  LANGUAGE_SELECTOR_OVERLAY: "language-selector-overlay",
  LANGUAGE_SELECTOR_OPTION: "language-selector__option",
};

class LanguageSelector {
  /**
   * Конструктор класса LanguageSelector
   * @param {HTMLElement} selector - DOM элемент селектора языка
   */
  constructor(selector) {
    this.selector = selector;
    this.trigger = selector.querySelector(SELECTORS.TRIGGER);
    this.dropdown = selector.querySelector(SELECTORS.DROPDOWN);
    this.options = selector.querySelectorAll(SELECTORS.OPTIONS);
    this.currentDisplay = selector.querySelector(SELECTORS.CURRENT);
    this.isOpen = false;

    this.init();
  }

  /**
   * Инициализация обработчиков событий и установка текущего языка
   */
  init() {
    // Устанавливаем текущий язык на основе куки pll_language
    this.setCurrentLanguageFromCookie();

    // Обработчик клика по триггеру для открытия/закрытия дропдауна
    this.trigger.addEventListener("click", this.toggleDropdown.bind(this));

    // Закрытие дропдауна при клике вне селектора
    document.addEventListener("click", this.handleClickOutside.bind(this));

    // Обработка выбора опции
    this.options.forEach((option) => {
      option.addEventListener("click", this.selectOption.bind(this, option));
    });

    // Обработка клавиатурной навигации
    this.trigger.addEventListener("keydown", this.handleKeydown.bind(this));
  }

  /**
   * Переключение состояния дропдауна (открыть/закрыть)
   */
  toggleDropdown() {
    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  /**
   * Открытие дропдауна с опциями языка
   */
  openDropdown() {
    this.isOpen = true;
    this.selector.classList.add(SELECTORS.LANGUAGE_SELECTOR_OPEN);
    this.dropdown.style.display = "block";

    // Управление фокусом для accessibility
    setTimeout(() => {
      this.options[0].focus();
    }, 10);
  }

  /**
   * Закрытие дропдауна с опциями языка
   */
  closeDropdown() {
    this.isOpen = false;
    this.selector.classList.remove(SELECTORS.LANGUAGE_SELECTOR_OPEN);
    this.dropdown.style.display = "none";
  }

  /**
   * Обработка выбора опции языка
   * Находит соответствующий элемент меню и имитирует клик по нему
   * @param {HTMLElement} option - Выбранная опция языка
   */
  selectOption(option) {
    const value = option.dataset.value;
    const flagSrc = option.querySelector("img").src;
    const langText = option.querySelector("span").textContent;

    // Обновляем отображение текущего выбранного языка
    this.currentDisplay.querySelector("img").src = flagSrc;
    this.currentDisplay.querySelector("span").textContent = langText;

    // Ищем соответствующий элемент меню языка и кликаем по нему
    this.triggerLanguageSwitch(value);

    // Закрываем дропдаун
    this.closeDropdown();

    // Возвращаем фокус на триггер
    this.trigger.focus();
  }

  /**
   * Получение значения куки по имени
   * @param {string} name - Имя куки
   * @returns {string|null} Значение куки или null, если кука не найдена
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(";").shift();
    }
    return null;
  }

  /**
   * Установка текущего языка в селекторе на основе куки pll_language
   * Читает значение куки и обновляет отображение селектора
   */
  setCurrentLanguageFromCookie() {
    const currentLang = this.getCookie("pll_language");

    if (!currentLang) {
      console.log("Кука pll_language не найдена");
      return;
    }

    // Находим опцию с соответствующим значением
    const targetOption = Array.from(this.options).find(
      (option) => option.dataset.value === currentLang,
    );

    if (targetOption) {
      // Обновляем отображение текущего выбранного языка
      const flagSrc = targetOption.querySelector("img").src;
      const langText = targetOption.querySelector("span").textContent;

      this.currentDisplay.querySelector("img").src = flagSrc;
      this.currentDisplay.querySelector("span").textContent = langText;

      console.log(`Текущий язык установлен: ${currentLang}`);
    } else {
      console.warn(`Опция для языка ${currentLang} не найдена в селекторе`);
    }
  }

  /**
   * Переключение языка путем клика по соответствующему элементу меню
   * Ищет элемент с классом lang-item-{lang} и кликает по ссылке внутри него
   * @param {string} lang - Код языка (ru, kz)
   */
  triggerLanguageSwitch(lang) {
    // Ищем элемент меню для выбранного языка
    const langMenuItem = document.querySelector(`.lang-item-${lang}`);

    if (langMenuItem) {
      // Находим ссылку внутри элемента меню
      const langLink = langMenuItem.querySelector(SELECTORS.DESKTOP_MENU_LINK);

      if (langLink) {
        // Имитируем клик по ссылке для переключения языка
        langLink.click();
        console.log(`Выполнен клик по ссылке языка: ${lang}`);
      } else {
        console.warn(`Ссылка для языка ${lang} не найдена в элементе меню`);
      }
    } else {
      console.warn(`Элемент меню для языка ${lang} не найден`);
    }

    // Также генерируем кастомное событие для дополнительных обработчиков
    const event = new CustomEvent("languageChange", {
      detail: { language: lang },
    });
    document.dispatchEvent(event);
  }

  /**
   * Обработка клика вне селектора для закрытия дропдауна
   * @param {Event} event - Событие клика
   */
  handleClickOutside(event) {
    if (!this.selector.contains(event.target)) {
      this.closeDropdown();
    }
  }

  /**
   * Обработка клавиатурных событий для accessibility и навигации
   * Поддерживает клавиши: Enter, Space, Escape, Arrow Up/Down
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  handleKeydown(event) {
    if (!this.isOpen) {
      // Если дропдаун закрыт, открываем его по Enter или Space
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.openDropdown();
      }
      return;
    }

    const focusedElement = document.activeElement;
    const currentIndex = Array.from(this.options).indexOf(focusedElement);

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        this.trigger.focus();
        break;

      case "ArrowDown":
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % this.options.length;
        this.options[nextIndex].focus();
        break;

      case "ArrowUp":
        event.preventDefault();
        const prevIndex =
          currentIndex <= 0 ? this.options.length - 1 : currentIndex - 1;
        this.options[prevIndex].focus();
        break;

      case "Enter":
      case " ":
        event.preventDefault();
        if (focusedElement.classList.contains(SELECTORS.LANGUAGE_SELECTOR_OPTION)) {
          this.selectOption(focusedElement);
        }
        break;
    }
  }
}

/**
 * Инициализация всех селекторов языка на странице после загрузки DOM
 */
document.addEventListener("DOMContentLoaded", function () {
  const selectors = document.querySelectorAll(SELECTORS.LANGUAGE_SELECTOR);
  selectors.forEach((selector) => {
    new LanguageSelector(selector);
  });
});

/**
 * Обработка открытия мобильного селектора с наложением overlay
 */
document.addEventListener("languageSelectorOpen", function (event) {
  const selector = event.detail.selector;
  if (selector.classList.contains(SELECTORS.LANGUAGE_SELECTOR_MOBILE)) {
    // Создаем overlay для мобильной версии
    const overlay = document.createElement("div");
    overlay.className = SELECTORS.LANGUAGE_SELECTOR_OVERLAY;
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.3);
      z-index: 999;
      backdrop-filter: blur(2px);
    `;

    overlay.addEventListener("click", function () {
      selector.querySelector(SELECTORS.TRIGGER).click();
      overlay.remove();
    });

    document.body.appendChild(overlay);
  }
});

/**
 * Обработка закрытия мобильного селектора с удалением overlay
 */
document.addEventListener("languageSelectorClose", function (event) {
  const overlay = document.querySelector(`.${SELECTORS.LANGUAGE_SELECTOR_OVERLAY}`);
  if (overlay) {
    overlay.remove();
  }
});
